import { request, ApiError } from '@/api/http';
import type {
  SearchExecution,
  SearchExecutionListParams,
  SearchExecutionListResponse,
  VehicleFilterEventListResponse,
} from '@/api/types';
import { normalizeSearchExecution } from '@/api/normalizeEntity';
import { filterValidUuids } from '@/utils/uuid';

type RequestOpts = { signal?: AbortSignal };

function buildQuery(params?: SearchExecutionListParams): string {
  const search = new URLSearchParams();
  if (params?.search_name) search.set('search_name', params.search_name);
  if (params?.source) search.set('source', params.source);
  if (params?.status) search.set('status', params.status);
  if (params?.debug_only) search.set('debug_only', 'true');
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  return search.toString();
}

export const searchExecutionsService = {
  list: async (params?: SearchExecutionListParams, opts?: RequestOpts) => {
    const q = buildQuery(params);
    const res = await request<SearchExecutionListResponse>(
      `/search-executions${q ? `?${q}` : ''}`,
      opts,
    );
    return { ...res, items: res.items.map(normalizeSearchExecution) };
  },
  get: async (id: string, opts?: RequestOpts) =>
    normalizeSearchExecution(await request<SearchExecution>(`/search-executions/${id}`, opts)),
  listVehicleFilters: (executionId: string, opts?: RequestOpts) =>
    request<VehicleFilterEventListResponse>(
      `/search-executions/${executionId}/vehicle-filters`,
      opts
    ),
  bulkDelete: (ids: string[]) => {
    const validIds = filterValidUuids(ids);
    if (validIds.length === 0) {
      throw new ApiError('No valid execution IDs to delete', 400);
    }
    return request<{ deleted: number; status?: string; failed?: number; errors?: { id: string; reason: string }[] }>(
      '/search-executions/bulk/delete',
      {
        method: 'POST',
        body: JSON.stringify({ ids: validIds }),
      },
    );
  },
  cleanupPreview: (payload: {
    older_than_days?: number;
    debug_only?: boolean;
    status?: string;
  }) =>
    request<{ matched: number }>('/search-executions/cleanup/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  cleanup: (payload: {
    older_than_days?: number;
    debug_only?: boolean;
    status?: string;
    limit?: number;
  }) =>
    request<{ deleted: number }>('/search-executions/cleanup', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  runDiagnosticsScrape: (payload: {
    source: string;
    search_name?: string;
    search_id?: string;
    force?: boolean;
  }) =>
    request<{
      search_id?: string | null;
      subscription_id?: string | null;
      source?: string | null;
      force?: boolean | null;
      jobs_queued: number;
      job_id?: string | null;
      status?: string | null;
      total_items?: number | null;
      warnings?: string[];
      seed_urls?: string[];
    }>('/search-executions/diagnostics/run', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};
